<template>
  <div :id="componentAttrIdAutosuggest" v-bind="$attrs">
    <slot name="before-input" />
    <div
      role="combobox"
      :aria-expanded="isOpen ? 'true' : 'false'"
      aria-haspopup="listbox"
      :aria-owns="`${componentAttrIdAutosuggest}-${componentAttrPrefix}__results`"
    >
      <input
        :value="internalValue"
        :class="[
          isOpen ? `${componentAttrPrefix}__input--open` : '',
          internalInputProps['class'],
        ]"
        v-bind="internalInputProps"
        aria-autocomplete="list"
        :aria-activedescendant="
          isOpen && selectedItem !== null
            ? `${componentAttrPrefix}__results-item--${selectedItem.index}`
            : ''
        "
        :aria-controls="`${componentAttrIdAutosuggest}-${componentAttrPrefix}__results`"
        @input="inputHandler"
        @keydown="handleKeyStroke"
        @focus="$emit('focus')"
        v-on="listeners"
      />
    </div>
    <slot name="after-input" />
    <div
      :id="`${componentAttrIdAutosuggest}-${componentAttrPrefix}__results`"
      :class="_componentAttrClassAutosuggestResultsContainer"
    >
      <div
        v-if="isOpen"
        :class="_componentAttrClassAutosuggestResults"
        :aria-labelledby="componentAttrIdAutosuggest"
      >
        <slot name="before-suggestions" />
        <component
          :is="cs.type"
          v-for="(cs, key) in computedSections"
          :ref="(el) => refSet(el, getSectionRef(`${cs.name}${key}`))"
          :key="getSectionRef(`${cs.name}${key}`)"
          :current-item="selectedItem"
          :normalize-item-function="normalizeItem"
          :render-suggestion="renderSuggestion"
          :section="cs"
          :component-attr-prefix="componentAttrPrefix"
          :component-attr-id-autosuggest="componentAttrIdAutosuggest"
          @update-current-item="updateCurrentItem"
        >
          <template
            v-if="$slots[`before-section-${cs.name || cs.label}`]"
            #[`before-section-${cs.name||cs.label}`]="{ section, className }"
            :key="index"
          >
            <slot
              :name="`before-section-${cs.name || cs.label}`"
              :section="section"
              :class-name="className"
            />
          </template>

          <template #default="{ suggestion, _key }">
            <slot :suggestion="suggestion" :index="_key">
              {{ suggestion.item }}
            </slot>
          </template>

          <template
            v-if="$slots[`after-section-${cs.name || cs.label}`]"
            #[`after-section-${cs.name||cs.label}`]="{ section }"
          >
            <slot
              :name="`after-section-${cs.name || cs.label}`"
              :section="section"
            />
          </template>

          <template v-if="$slots['after-section']" #after-section="{ section }">
            <slot name="after-section" :section="section" />
          </template>
        </component>
        <slot name="after-suggestions" />
      </div>
      <slot name="after-suggestions-container" />
    </div>
  </div>
</template>

<script setup lang="ts">
/* eslint-disable @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-return,
   @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access --
   TODO: Properly type Function props (getSuggestionValue, shouldRenderSuggestions, onSelected, click)
   These are callback props with intentionally flexible signatures */
import type { Component } from 'vue'
import {
  computed,
  onBeforeMount,
  onMounted,
  onUnmounted,
  ref,
  toRefs,
  useAttrs,
  watch,
} from 'vue'
import DefaultSection from '~/apps/search-wizard/campsites-search/location/components/DefaultSection'

// Type definitions
interface SuggestionItem {
  name?: string
  [key: string]: unknown
}

interface SuggestionSection {
  name?: string
  label?: string
  data: SuggestionItem[]
}

interface SectionConfig {
  limit?: number
  type?: Component
  label?: string
  ulClass?: string
  liClass?: string
  onSelected?: (
    item: SelectedItem | null,
    term: string | null,
    selectingDefault?: boolean,
  ) => void
}

interface ComputedSection {
  name: string
  label?: string
  type: Component
  limit: number
  data: SuggestionItem[]
  start_index: number
  end_index: number
  ulClass?: string
  liClass?: string
}

interface SelectedItem {
  name: string
  type: Component
  label?: string
  liClass?: string
  item: SuggestionItem
  index?: number
}

interface ChildSectionRef {
  section: { label?: string; liClass?: string }
  getItemByIndex: (index: number) => SuggestionItem
}

const $refs = ref<Record<string, ChildSectionRef | undefined>>({})
const refSet = (el: ChildSectionRef | null, key: string) => {
  if (el) {
    $refs.value[key] = el
  }
  return key
}

const attrs = useAttrs() as Record<string, unknown>
const emit = defineEmits<{
  'update:modelValue': [value: string]
  'item-changed': [item: SelectedItem | null]
  opened: []
  closed: []
  focus: []
  blur: []
}>()

const defaultSectionConfig = {
  name: 'default',
  type: DefaultSection,
}

//
// Props
//
const props = defineProps({
  modelValue: {
    type: String,
    default: null,
  },
  /** v-binds to the <input /> tag for fine-grain control */
  inputProps: {
    type: Object,
    required: true,
  },
  /** limits the number of suggestions for all sections */
  limit: {
    type: Number,
    required: false,
    default: Infinity,
  },
  suggestions: {
    type: Array,
    required: true,
  },
  renderSuggestion: {
    type: Function,
    required: false,
    default: null,
  },
  getSuggestionValue: {
    type: Function,
    required: false,
    default: (suggestion) => {
      const item = suggestion.item
      if (typeof item === 'object' && item.name) {
        return item.name
      } else {
        return item
      }
    },
  },
  shouldRenderSuggestions: {
    type: Function,
    required: false,
    default: (totalResults, loading) => {
      return totalResults > 0 && !loading
    },
  },
  sectionConfigs: {
    type: Object,
    required: false,
    default: () => {
      return {
        default: {
          onSelected: null,
        },
      }
    },
  },
  componentAttrIdAutosuggest: {
    type: String,
    required: false,
    default: 'autosuggest',
  },
  componentAttrClassAutosuggestResultsContainer: {
    type: String,
    required: false,
    default: null, // `${componentAttrPrefix}__results-container`
  },
  componentAttrClassAutosuggestResults: {
    type: String,
    required: false,
    default: null, // `${componentAttrPrefix}__results`
  },
  componentAttrPrefix: {
    type: String,
    required: false,
    default: 'autosuggest',
  },
  currentItem: {
    type: Object,
    default: null,
  },
})

const {
  modelValue,
  suggestions,
  componentAttrIdAutosuggest,
  componentAttrPrefix,
  inputProps,
  renderSuggestion,
  componentAttrClassAutosuggestResults,
  componentAttrClassAutosuggestResultsContainer,
  sectionConfigs,
  shouldRenderSuggestions,
  getSuggestionValue,
} = toRefs(props)

//
// Data
//
const internalValue = ref<string | null>(null)
const searchInputOriginal = ref<string | null>(null)
const selectedItem = ref<SelectedItem | null>(null)
// TODO use event states instead of generic "loading" variable
/** Helps with making sure the dropdown doesn't stay open after certain actions */
const loading = ref(false)
const didSelectFromOptions = ref(false)
const didSelectWithKeyboard = ref(false)
const defaultInputProps = {
  type: 'text',
  autocomplete: 'off',
  autocorrect: 'off',
  spellcheck: false,
}
const clientXMouseDownInitial = ref<number | null>(null)

//
// Computed
//
const internalInputProps = computed(() => {
  return {
    ...defaultInputProps,
    ...inputProps.value,
  }
})

const listeners = computed(() => {
  return {
    input: (_e: Event) => {
      // Don't do anything native here, since we have inputHandler
    },
    /**
     * Wrap native click handler to allow for added behavior
     */
    click: () => {
      loading.value = false
      if (typeof attrs.click === 'function') {
        attrs.click(selectedItem.value)
      }
    },
    selected: (_didSelect: boolean, selectingDefault: boolean) => {
      /**
       * Determine which onSelected to fire. This can be either from inside
       * a section's object, from the `@selected` event
       */
      const configs = sectionConfigs.value as Record<string, SectionConfig>
      const selectedConfig = selectedItem.value
        ? configs[selectedItem.value.name]
        : undefined
      if (selectedItem.value && selectedConfig?.onSelected) {
        selectedConfig.onSelected(
          selectedItem.value,
          searchInputOriginal.value,
          selectingDefault,
        )
      } else if (configs.default?.onSelected) {
        configs.default.onSelected(
          null,
          searchInputOriginal.value,
          selectingDefault,
        )
      } else if (typeof attrs.onSelected === 'function') {
        attrs.onSelected(
          selectedItem.value,
          selectedItem.value?.index,
          selectingDefault,
        )
      }
      setChangeItem(null)
    },
  }
})

/** Capture mousedown position so we can use it to detect if the scrollbar was clicked */
const onDocumentMouseDown = (e: MouseEvent) => {
  const target = e.target as Element | null
  const rect = target?.getBoundingClientRect?.() ?? { left: 0 }
  clientXMouseDownInitial.value = e.clientX - rect.left
}

/** 'mouseup' event handler */
const onDocumentMouseUp = (e: MouseEvent) => {
  const target = e.target as Element | null
  /** Do not re-render list on input click */
  const isChild = target && document.contains(target)

  /* Clicks outside of dropdown */
  if (!isChild) {
    loading.value = true
    selectedItem.value = null
    return
  }

  if (
    (target as HTMLElement)?.tagName === 'INPUT' ||
    clickedOnScrollbar(e, clientXMouseDownInitial.value)
  ) {
    return
  }

  /** Selects an item in the dropdown */
  loading.value = true
  didSelectFromOptions.value = true
  setChangeItem(selectedItem.value, true)
  listeners.value.selected(true, false)
  emit('blur')
}

const addDocumentListeners = () => {
  document.addEventListener('mouseup', onDocumentMouseUp)
  document.addEventListener('mousedown', onDocumentMouseDown)
}

const removeDocumentListeners = () => {
  if (document) {
    document.removeEventListener('mouseup', onDocumentMouseUp)
    document.removeEventListener('mousedown', onDocumentMouseDown)
  }
}

/**
 * @returns {Boolean}
 */
const isOpen = computed(() => {
  const isOpenValue = shouldRenderSuggestions.value(
    totalResults.value,
    loading.value,
  ) as boolean
  if (isOpenValue && import.meta.client) {
    addDocumentListeners()
  } else if (import.meta.client) {
    removeDocumentListeners()
  }

  return isOpenValue
})

/**
 * Normalize suggestions into sections based on defaults and section
 * configs.
 */
const computedSections = computed((): ComputedSection[] => {
  let tmpSize = 0
  const configs = sectionConfigs.value as Record<string, SectionConfig>

  const result = (suggestions.value as SuggestionSection[])
    .filter((section): section is SuggestionSection => Boolean(section?.data))
    .map((section) => {
      const name = section.name ?? defaultSectionConfig.name
      const config = configs[name]

      const _type = config?.type ?? defaultSectionConfig.type
      let _limit = config?.limit ?? props.limit ?? Infinity
      _limit = section.data.length < _limit ? section.data.length : _limit
      const _label = config?.label ?? section.label

      const computedSection: ComputedSection = {
        name,
        label: _label,
        type: _type,
        limit: _limit,
        data: section.data,
        start_index: tmpSize,
        end_index: tmpSize + _limit - 1,
        ulClass: config?.ulClass,
        liClass: config?.liClass,
      }

      tmpSize += _limit

      return computedSection
    })
  return result
})

/**
 * Calculate number of results in each section.
 * @returns {Number}
 */
const totalResults = computed(() => {
  return computedSections.value.reduce((acc, section) => {
    // For each section, make sure we calculate the size
    // based on how many are rendered, which maxes out at
    // the limit but can be less than the limit.
    if (!section) {
      return acc
    }
    const { limit, data } = section
    return acc + (data.length >= limit ? limit : data.length)
  }, 0)
})

const _componentAttrClassAutosuggestResultsContainer = computed(() => {
  return (
    componentAttrClassAutosuggestResultsContainer.value ||
    `${componentAttrPrefix.value}__results-container`
  )
})

const _componentAttrClassAutosuggestResults = computed(() => {
  return (
    componentAttrClassAutosuggestResults.value ||
    `${componentAttrPrefix.value}__results`
  )
})

//
// WATCH
//
watch(modelValue, (newValue) => {
  internalValue.value = newValue
})

/** Emits opened/closed events */
// Use flush: 'sync' to emit immediately when isOpen changes
// This prevents the watch from running in a separate browser task
// which would extend INP measurement
// Use flush: 'sync' to emit immediately when isOpen changes.
// This prevents the watch from running in a separate browser task
// which would extend INP measurement.
watch(
  isOpen,
  (newValue, oldValue) => {
    if (newValue !== oldValue) {
      if (newValue) {
        emit('opened')
      } else {
        emit('closed')
      }
    }
  },
  { flush: 'sync' },
)

//
// METHODS
//

/** Handler for @input events to support v-model behavior */
const inputHandler = (e: Event) => {
  const target = e.target as HTMLInputElement
  const newValue = target.value
  emit('update:modelValue', newValue)
  internalValue.value = newValue
  if (!didSelectFromOptions.value) {
    searchInputOriginal.value = newValue
    selectedItem.value = null
  }
}

/** Helper for making sure the sectionRef getter is consistent */
const getSectionRef = (i: string | number): string => {
  return 'computed_section_' + i
}

/**
 * Helper for getting a suggestion item by index.
 * @returns {ResultItem}
 */
const getItemByIndex = (index: number): SelectedItem | false => {
  let obj: SelectedItem | false = false
  for (const [i, section] of computedSections.value.entries()) {
    if (index >= section.start_index && index <= section.end_index) {
      const trueIndex = index - section.start_index
      const sectionName = section.name
      const childSection = $refs.value[getSectionRef(`${sectionName}${i}`)]

      if (childSection) {
        obj = normalizeItem(
          section.name,
          section.type,
          childSection.section.label,
          childSection.section.liClass,
          childSection.getItemByIndex(trueIndex),
          index, // Pass the index for tracking
        )
        break
      }
    }
  }

  return obj
}

/**
 * Handler for 'keydown' event. Does a number of things, including making
 * sure to ignore keycodes, ensure items are visible and also that the input
 * value is updated/reset according to where the user has keyed to.
 * @param {MouseEvent} e
 * @returns {void}
 */
const handleKeyStroke = (e: KeyboardEvent) => {
  const { key } = e
  const ignoredKeys = ['Shift', 'Tab', 'Control', 'Alt', 'Meta']
  if (ignoredKeys.includes(key)) {
    return
  }

  loading.value = false
  didSelectFromOptions.value = false
  if (isOpen.value) {
    // Use stored index instead of expensive findIndex + isEqual
    // This avoids O(n) deep comparison on every arrow key press
    const currentIndex = selectedItem.value?.index ?? -1
    const direction = key === 'ArrowDown' ? 1 : -1
    const newIndex = currentIndex + direction

    switch (key) {
      case 'ArrowDown':
      case 'ArrowUp':
        e.preventDefault()
        if (newIndex >= 0 && newIndex < totalResults.value) {
          const newItem = getItemByIndex(newIndex)
          updateCurrentItem(newItem)
        } else if (newIndex < 0) {
          // TODO maybe reset to original user input when proper text searching is enabled
        }
        break

      case 'Enter':
        if (selectedItem.value) {
          e.preventDefault()
          setChangeItem(selectedItem.value, true)
        }
        didSelectFromOptions.value = true
        didSelectWithKeyboard.value = true
        loading.value = true
        listeners.value.selected(didSelectFromOptions.value, false)
        break

      case 'Escape':
        /**
         * For 'search' input type, make sure the browser doesn't clear the
         * input when Escape is pressed.
         */
        loading.value = true
        selectedItem.value = null
        internalValue.value = searchInputOriginal.value
        emit('update:modelValue', searchInputOriginal.value ?? '')
        e.preventDefault()
        break
    }
  }
}

/**
 * Wrapper around currentItem setter to emit events and ensure to update the
 * searchInputOriginal when a user selects an option.
 * @param {ResultItem} item
 * @param {Boolean} overrideOriginalInput determine if the 'saved' original
 *   input should be updated. When a user selects an option, this will be
 *   updated, but if a user keys into the <input/> then the input will be
 *   reset to the searchInputOriginal.
 * @return {void}
 */
const setChangeItem = (
  item: SelectedItem | null,
  overrideOriginalInput = false,
) => {
  if (selectedItem.value === null || !item) {
    selectedItem.value = null
    emit('item-changed', null)
  } else {
    selectedItem.value = item
    const v = getSuggestionValue.value(item) as string
    internalValue.value = v
    if (overrideOriginalInput) {
      searchInputOriginal.value = v
    }
    emit('item-changed', item)
  }
}

/** Function to standardize suggestion item object picked from sections */
const normalizeItem = (
  name: string,
  type: Component,
  label: string | undefined,
  className: string | undefined,
  item: SuggestionItem,
  index?: number,
): SelectedItem => {
  return {
    name,
    type,
    label,
    liClass: (item.liClass as string) || className,
    item,
    index,
  }
}

/** Updates the currently selected item from keyboard navigation */
const updateCurrentItem = (item: SelectedItem | false) => {
  if (item) {
    selectedItem.value = item
    setChangeItem(item)
    internalValue.value = searchInputOriginal.value
  } else {
    selectedItem.value = null
  }
}

/** Helper to detect if the user clicked on the scrollbar */
const clickedOnScrollbar = (e: MouseEvent, mouseX: number | null): boolean => {
  const results = document.querySelector(
    `.${_componentAttrClassAutosuggestResults.value}`,
  )
  if (!results || mouseX === null) {
    return false
  }

  const target = e.target as Element | null
  const mouseIsInsideScrollbar =
    results.clientWidth <= mouseX + 17 &&
    mouseX + 17 <= results.clientWidth + 34

  return target?.tagName === 'DIV' && mouseIsInsideScrollbar
}

onBeforeMount(() => {
  loading.value = true
})

onMounted(() => {
  internalValue.value = modelValue.value
  // addDocumentListeners()
})

onUnmounted(() => {
  if (removeDocumentListeners) {
    removeDocumentListeners()
  }
})
</script>
